import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auth
export const authMe = () => http.get("/auth/me").then((r) => r.data);
export const authSession = (sessionId) =>
  http.post("/auth/session", null, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data);
export const authLogout = () => http.post("/auth/logout").then((r) => r.data);

// Admin
export const adminListMembers = () => http.get("/admin/users").then((r) => r.data);
export const adminInvite = (email, role) => http.post("/admin/users", { email, role }).then((r) => r.data);
export const adminChangeRole = (userId, role) => http.patch(`/admin/users/${userId}`, { role }).then((r) => r.data);
export const adminRevoke = (userId) => http.delete(`/admin/users/${userId}`).then((r) => r.data);
export const adminCancelInvite = (email) => http.delete("/admin/invitations", { params: { email } }).then((r) => r.data);
export const adminResendInvite = (email) => http.post("/admin/invitations/resend", null, { params: { email } }).then((r) => r.data);
export const adminWipeEngagements = (confirm, dryRun = false) =>
  http.post("/admin/wipe-engagements", { confirm, dry_run: dryRun }).then((r) => r.data);

// Clients
export const listClients = (archived = false) =>
  http.get("/clients", { params: { archived } }).then((r) => r.data);
export const createClient = (payload) => http.post("/clients", payload).then((r) => r.data);
export const getClient = (id) => http.get(`/clients/${id}`).then((r) => r.data);
export const updateClient = (id, payload) => http.patch(`/clients/${id}`, payload).then((r) => r.data);
export const archiveClient = (id, archived = true) => http.patch(`/clients/${id}`, { archived }).then((r) => r.data);

// Consolidated
export const getConsolidated = (clientId, period) =>
  http.get(`/clients/${clientId}/consolidated`, { params: { period } }).then((r) => r.data);
export const exportConsolidatedUrl = (clientId, period) =>
  `${API}/clients/${clientId}/consolidated/export?period=${encodeURIComponent(period)}`;

// Runs
export const listRuns = (params = {}) =>
  http.get("/runs", { params: { archived: false, ...params } }).then((r) => r.data);

export const uploadRun = ({ jsonFile, xlsxFile, clientId, period, divisionId, scopeKind, divisionIds, gstinGroupId, confirmMismatch, onProgress }) => {
  const fd = new FormData();
  fd.append("accounting_json", jsonFile);
  fd.append("ledger_xlsx", xlsxFile);
  fd.append("client_id", clientId);
  fd.append("period", period);
  if (divisionId) fd.append("division_id", divisionId);
  // Phase C.1 — optional scope passthrough (backend defaults to
  // consolidation when absent).
  if (scopeKind) fd.append("scope_kind", scopeKind);
  if (Array.isArray(divisionIds) && divisionIds.length > 0) {
    fd.append("division_ids", divisionIds.join(","));
  }
  if (gstinGroupId) fd.append("gstin_group_id", gstinGroupId);
  if (confirmMismatch) fd.append("confirm_mismatch", "true");
  return http.post("/runs", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
  }).then((r) => r.data);
};

export const archiveRun = (id) => http.post(`/runs/${id}/archive`).then((r) => r.data);
export const getRun = (id) => http.get(`/runs/${id}`).then((r) => r.data);
export const generateRun = (id, payload) => http.post(`/runs/${id}/generate`, payload).then((r) => r.data);
export const getTransactions = (id, bucket, ledger, party) =>
  http.get(`/runs/${id}/transactions`, { params: { bucket, ledger, party } }).then((r) => r.data);
export const saveSelections = (id, payload) =>
  http.patch(`/runs/${id}/selections`, payload).then((r) => r.data);
export const exportRunUrl = (id) => `${API}/runs/${id}/export`;
export const exportRunMappingSnapshotUrl = (id) => `${API}/runs/${id}/mapping-export`;
export const fetchExemptPool = (id, itcLedgers) =>
  http.post(`/runs/${id}/exempt-pool`, { itc_ledgers: itcLedgers || [] }).then((r) => r.data);

// Party Registration Status — GST registration review/override, scoped
// to the client so a correction also applies to future periods.
export const getPartyRegistration = (id) =>
  http.get(`/runs/${id}/party-registration`).then((r) => r.data);
export const setPartyRegistrationOverride = (id, payload) =>
  http.post(`/runs/${id}/party-registration/override`, payload).then((r) => r.data);
export const clearPartyRegistrationOverride = (id, partyName) =>
  http.post(`/runs/${id}/party-registration/clear`, { party_name: partyName }).then((r) => r.data);


// Run rerun — re-pin to latest Library versions, re-parse, reset `generated`.
export const rerunRun = (id) => http.post(`/runs/${id}/rerun`).then((r) => r.data);

// Client Library — file-store endpoints.
export const getLibraryCatalog = () => http.get("/library/catalog").then((r) => r.data);
export const getLibraryStatus = (clientId, period, division = null) =>
  http.get(`/library/clients/${clientId}/status`, { params: { period, division } }).then((r) => r.data);
export const listLibraryFiles = (clientId, period, includeDeleted = false) =>
  http.get(`/library/clients/${clientId}/files`, { params: { period, include_deleted: includeDeleted } }).then((r) => r.data);
export const uploadLibraryFile = ({ file, clientId, period, division = null, divisionIds = null, fileType, onProgress }) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("client_id", clientId);
  fd.append("period", period);
  if (division) fd.append("division", division);
  if (Array.isArray(divisionIds) && divisionIds.length > 0) {
    fd.append("division_ids", divisionIds.join(","));
  }
  fd.append("file_type", fileType);
  return http.post("/library/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
  }).then((r) => r.data);
};
export const deleteLibraryFile = (fileId) => http.delete(`/library/files/${fileId}`).then((r) => r.data);
export const restoreLibraryFile = (fileId) => http.post(`/library/files/${fileId}/restore`).then((r) => r.data);
export const downloadLibraryFileUrl = (fileId) => `${API}/library/files/${fileId}/download`;
export const downloadLibraryTemplateUrl = (clientId, fileType, period, division = null) => {
  const params = new URLSearchParams({ period });
  if (division) params.set("division", division);
  return `${API}/library/clients/${clientId}/template/${fileType}?${params.toString()}`;
};
